import { pathToGeoJSON } from "@ptx-autonomous/libs/api";
import type {
	LineString,
	PlannedPathCollection,
	SimplePoint,
} from "@ptx-autonomous/libs/api-models";
import along from "@turf/along";
import distance from "@turf/distance";
import { lineString, point } from "@turf/helpers";

export function interpolatePlannedPath(plannedPath: PlannedPathCollection[]) {
	// Interpolate points into the planned path if the distance between any two points is greater than 500m
	const originalPoints = plannedPath[0].PlannedPath as SimplePoint[];

	const lineStringPath = pathToGeoJSON(plannedPath)[0];
	const plannedPathCoords = lineString(lineStringPath.coordinates);
	const minDistanceKm = 0.5;

	const newCoords: number[][] = [];
	const newSimplePoints: SimplePoint[] = [];

	const coordKey = (coord: number[]) =>
		`${coord[0].toFixed(6)}_${coord[1].toFixed(6)}`;
	const originalLookup = new Map<string, SimplePoint>();
	originalPoints.forEach(pt => {
		originalLookup.set(coordKey([pt.Long, pt.Lat]), pt);
	});

	for (
		let i = 0;
		i < plannedPathCoords.geometry.coordinates.length - 1;
		i++
	) {
		const startCoord = plannedPathCoords.geometry.coordinates[i];
		const endCoord = plannedPathCoords.geometry.coordinates[i + 1];

		const origPoint = originalLookup.get(coordKey(startCoord));
		newCoords.push(startCoord);
		newSimplePoints.push({
			Lat: startCoord[1],
			Long: startCoord[0],
			Label: origPoint ? origPoint.Label : "",
			Alt: origPoint ? origPoint.Alt : 0,
		});

		const segmentLength = distance(point(startCoord), point(endCoord), {
			units: "kilometers",
		});
		if (segmentLength > minDistanceKm) {
			const numInterpolations = Math.floor(segmentLength / minDistanceKm);
			for (let j = 1; j <= numInterpolations; j++) {
				const interpolatedPoint = along(
					lineString([startCoord, endCoord]),
					j * minDistanceKm,
					{ units: "kilometers" },
				);
				newCoords.push(interpolatedPoint.geometry.coordinates);
				// Interpolated points don't have a label -- assumed to be part of a swath
				newSimplePoints.push({
					Lat: interpolatedPoint.geometry.coordinates[1],
					Long: interpolatedPoint.geometry.coordinates[0],
					Label: "",
					Alt: 0,
				});
			}
		}
	}

	const lastCoord =
		plannedPathCoords.geometry.coordinates[
			plannedPathCoords.geometry.coordinates.length - 1
		];
	const lastOrig = originalLookup.get(coordKey(lastCoord));
	newCoords.push(lastCoord);
	newSimplePoints.push({
		Lat: lastCoord[1],
		Long: lastCoord[0],
		Label: lastOrig ? lastOrig.Label : "",
		Alt: lastOrig ? lastOrig.Alt : 0,
	});

	const newPlannedPath: LineString = {
		type: "LineString",
		coordinates: newCoords,
	};

	return { newPlannedPath, newSimplePoints };
}